import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    BookOpenIcon,
    CubeTransparentIcon,
    PlayCircleIcon,
    BuildingLibraryIcon,
    RocketLaunchIcon,
    StarIcon,
    BeakerIcon,
    WrenchScrewdriverIcon,
    PresentationChartBarIcon,
    DocumentMagnifyingGlassIcon,
    CpuChipIcon,
    BoltIcon,
    CheckBadgeIcon,
    Cog6ToothIcon,
    AcademicCapIcon,
    ScaleIcon,
    BanknotesIcon,
    SunIcon,
    CalendarDaysIcon,
    MapIcon,
    UserCircleIcon,
    ArrowRightOnRectangleIcon,
    Bars3Icon,
    XMarkIcon,
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
    CommandLineIcon,
    LightBulbIcon,
    ChatBubbleBottomCenterTextIcon,
    ShareIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { usePayment } from '../context/PaymentContext';
import { useSidebar } from '../context/SidebarContext';
import { logout } from '../services/authService';
import { track, EVENTS } from '../services/analytics';
import usePopularQuestions from '../hooks/usePopularQuestions';
import AstraAvatar from './concierge/AstraAvatar';
import { OPEN_ASTRA_EVENT, ASTRA_PATH } from '../utils/astraLink';
import useShareNotes from '../hooks/useShareNotes';
import { useAstraToday, openMoves, topMoveId, thingsLine } from '../services/astraToday';
import { BUILD_NAMES } from '../content/buildNames';

// Single Navigation component used by:
//   1. AppLayout (protected pages — Profile/Discover/Launchpad/Roadmap/...)
//   2. ResourcesPage and ResourcePaperPage (public pages — signed-in visitors get
//      the in-app rail; logged-out visitors get MarketingHeader instead)
//
// #231 — converted from a top bar into a collapsible LEFT sidebar:
//   • Desktop (lg+): a fixed left rail. Expanded = icons + labels (240px);
//     collapsed = icon-only rail (64px) with hover/focus tooltips. A chevron
//     toggle flips between them; the choice persists (SidebarContext → localStorage).
//   • Mobile (<lg): an off-canvas drawer opened from a slim sticky top bar
//     (hamburger). Tapping a link or the backdrop closes it.
//
// Auth-aware: links with `requiresAuth: true` are hidden when logged out. The
// footer shows the user + Upgrade + Sign out when signed in, or Sign in / Try
// Stratia when logged out.

// Ordered as the landing's story (founder pick B, 2026-10-22, #5183): your
// story, where it fits, your path to the decision, then the tools.
//   You      - Astra, your profile, sharing
//   Explore  - majors, activities, colleges, programs, visits
//   Plan     - the list, the roadmap, the notebook
//   Decide   - decisions and aid
//   Build    - Brainstorm and its three steps
//   Connect  - an agent of your own and the Playbook
//   Learn    - the reference library, for a logged-out render only
//
// Signed in, the rail has no Learn group (founder, 2026-10-24, #5309, Desk
// request R-32): "it is available free on the landing page and also it is
// provided in every page based on its relevance". Each page's hero links the
// paper that explains it (ValueSpotlight learnMore), so the library is reached
// from where it helps; /resources itself and the public footer link stay.
const NAV_SECTIONS = [
    {
        // 'You' (founder call 2026-07-31): the old 'Your profile' label
        // named its own first item — a group that says nothing the row
        // doesn't. One word also matches the item register (Majors,
        // Activities, Strategy) and reads as the journey's first beat:
        // You → Explore → Plan → Decide.
        label: 'You',
        links: [
            { path: '/profile', label: 'Profile', icon: UserCircleIcon, color: '#2563EB', requiresAuth: true },
            { path: '/share', label: 'Sharing', icon: ShareIcon, color: '#0F766E', requiresAuth: true },
        ],
    },
    {
        // The sidebar reads top-to-bottom as the student journey (founder call
        // 2026-07-21): tell your story -> EXPLORE options -> PLAN the list ->
        // APPLY. Explore = every discovery catalog/recommender: majors,
        // activities, colleges, and summer programs (its deadlines flow into
        // the Roadmap on their own).
        label: 'Explore',
        links: [
            { path: '/major-map', label: 'Majors', icon: AcademicCapIcon, color: '#7C3AED', requiresAuth: true },
            { path: '/activities', label: 'Activities', icon: LightBulbIcon, color: '#EAB308', requiresAuth: true },
            { path: '/universities', label: 'Universities', icon: BuildingLibraryIcon, color: '#0891B2', requiresAuth: true },
            { path: '/programs', label: 'Programs', icon: SunIcon, color: '#F59E0B', requiresAuth: true },
            { path: '/events', label: 'Visits & Events', icon: CalendarDaysIcon, color: '#0E7490', requiresAuth: true },
        ],
    },
    {
        // Deciding + scheduling: the list (Launchpad), the shareable plan it
        // produces (Strategy Report), and that plan on a calendar (Roadmap).
        // There is deliberately NO "Apply" stage — submission happens in the
        // Common App / university portals, not here (founder call 2026-07-21):
        // Stratia's job is exploring, planning, and deciding.
        label: 'Plan',
        links: [
            { path: '/launchpad', label: 'Launchpad', icon: RocketLaunchIcon, color: '#EA580C', requiresAuth: true },
            { path: '/roadmap', label: 'Roadmap', icon: MapIcon, color: '#D97706', requiresAuth: true },
            // The Notebook holds research about the schools on the list, linked
            // to those schools, and turns into roadmap tasks: planning
            // material, whatever produced it. Moved here from Agents on the
            // founder's call (2026-09-17, #2281).
            { path: '/research', label: 'Notebook', icon: BeakerIcon, color: '#059669', requiresAuth: true },
        ],
    },
    {
        // The journey's last stage: log decisions as they land, compare the
        // money, choose. Also gives /financial-aid its first nav home — it
        // was previously reachable only through in-app links.
        label: 'Decide',
        links: [
            { path: '/decision-ledger', label: 'Decisions', icon: ScaleIcon, color: '#DB2777', requiresAuth: true },
            { path: '/financial-aid', label: 'Aid', icon: BanknotesIcon, color: '#059669', requiresAuth: true },
        ],
    },
    {
        // Build sits below the path, with the other tools (founder pick B,
        // 2026-10-22, #5183, Desk request R-1). It had its own section above
        // Explore since 2026-09-21 (pick 1A, #2296); the landing now tells one
        // story (#5128: "Start with your story. See where it fits." then your
        // path, the builder tools lowered to one band), and the nav tells the
        // same one: You -> Explore -> Plan -> Decide, then Build, Connect and
        // Learn. A (Build between Plan and Decide) lost because it split the
        // path for a side track; C (Build as one row) because it put the steps
        // students click most one tap deeper. Real students, Sep 22 to Oct 22:
        // Profile and Launchpad 17 people each, every Build page 4 or fewer,
        // and Launchpad sat below the first screen behind the Build rows.
        //
        // The home (/builder) leads and the three steps nest under it as its
        // children (#2394). The children's order (patent, research, pitch) is
        // PRESENTATIONAL ONLY: after Brainstorm the steps are peers and none
        // is next (founder 2026-09-28, #2396). No row carries a marker: "Start
        // here", "7 of 7" and "New" were removed on the same pick (the start is
        // the student's story, and a launch pill retires). A free account
        // still sees the group's one Pass tag (founder, 2026-09-28). Every
        // name here is content/buildNames.js's: the founder's final names are
        // a one-file change there, never here.
        label: BUILD_NAMES.group,
        links: [
            {
                path: '/builder', label: BUILD_NAMES.home.label, icon: CubeTransparentIcon, color: '#9A7B2F', requiresAuth: true,
                children: [
                    // Every Pass-only row carries `pass: true` (#2358); `lab` is its step key.
                    { path: '/patent-lab', label: BUILD_NAMES.steps.patent.label, icon: WrenchScrewdriverIcon, color: '#9A7B2F', requiresAuth: true, pass: true, lab: 'patent' },
                    // The Research Lab (#2378, ADR 0017): Season Pass only.
                    { path: '/research-lab', label: BUILD_NAMES.steps.research.label, icon: DocumentMagnifyingGlassIcon, color: '#9A7B2F', requiresAuth: true, pass: true, lab: 'research' },
                    // The Pitch Lab (#2358): Season Pass only.
                    { path: '/pitch-lab', label: BUILD_NAMES.steps.pitch.label, icon: PresentationChartBarIcon, color: '#9A7B2F', requiresAuth: true, pass: true, lab: 'pitch' },
                ],
            },
        ],
    },
    {
        // The group is Connect (founder, 2026-09-17, #2281): link an agent,
        // then run the Playbook against your own data. Routes are unchanged
        // (/connect, /command-center); only the words moved. What the agents
        // produce lands in the Notebook, under Plan.
        label: 'Connect',
        links: [
            { path: '/connect', label: 'Agents', icon: CpuChipIcon, color: '#4F46E5', requiresAuth: true },
            // The Playbook retires with astra_popular_questions on (#5258,
            // founder answer 1): its prompts live on Connect, Ask Astra first.
            { path: '/command-center', label: 'Playbook', icon: CommandLineIcon, color: '#CA8A04', requiresAuth: true, retiresWithPopular: true },
        ],
    },
    {
        label: 'Learn',
        links: [
            // publicOnly: shown only when no one is signed in (#5309).
            { path: '/resources', label: 'Resources', icon: BookOpenIcon, color: '#0D9488', requiresAuth: false, publicOnly: true },
        ],
    },
];

// The sidebar's own destinations — the ONLY pages the app-level journey
// banners may appear on (founder, 2026-08-09). Derived from NAV_SECTIONS so
// the two can never drift.
export const NAV_TOP_LEVEL_PATHS = new Set(
    NAV_SECTIONS.flatMap((s) => s.links.filter((l) => !l.publicOnly)
        .flatMap((l) => [l.path, ...(l.children || []).map((c) => c.path)])).concat(['/']),
);

/** A single nav row — icon always; label hidden (with tooltip) when collapsed.
 *  Icon is monochrome (muted sage → brand green on active/hover); the active
 *  row gets a light green pill and a semibold label (the dark fill belongs to
 *  the counselor row alone, founder pick B 2026-09-15).
 *  Compact density (13px labels, 18px icons — the Linear/Notion register):
 *  15 links + 6 groups + footer must fit a laptop viewport without scrolling
 *  the rail. */
// `ariaLabel` gives a row a fuller accessible name than its visible label
// (Sharing's unread-notes count, which the pill shows to sighted readers).
// `badge` is the gold pill (today only Sharing's unread-notes count).
// `nested` indents a child row under its parent on the expanded rail (20px:
// measured 2026-09-27, at 28px a step's label sat three pixels short of one
// line beside a pill on the 240px rail); the collapsed rail shows every
// icon flush.
function NavItem({ to, label, icon: Icon, color, active, collapsed, onNavigate, ariaLabel, badge, nested }) {
    return (
        <Link
            to={to}
            onClick={onNavigate}
            title={collapsed ? label : undefined}
            aria-label={ariaLabel || label}
            aria-current={active ? 'page' : undefined}
            className={`group relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] transition-all
                ${nested ? 'pl-5' : ''}
                ${collapsed ? 'lg:justify-center lg:px-0' : ''}
                ${
                    active
                        ? 'bg-[#F0F7F1] text-[#1A4D2E] font-semibold'
                        : 'font-medium text-[#4A4A4A] hover:bg-[#F8F6F0] hover:text-[#1A4D2E]'
                }`}
        >
            {/* Identity-colored icons (founder, 2026-08-09: "build the mock as
                is" — supersedes the 2026-07-21 monochrome call). Each item
                wears its own hue at rest; on the ACTIVE pill the icon goes
                brand green. The pill is light since 2026-09-15 (founder pick
                B): the counselor row is the only dark green in the rail, so
                the active page and Astra never compete. */}
            <Icon
                className="h-[18px] w-[18px] shrink-0 transition-colors"
                style={{ color: active ? '#1A4D2E' : (color || '#9AA8A0') }}
                aria-hidden="true"
            />
            <span className={collapsed ? 'lg:hidden' : ''}>{label}</span>
            {badge ? (
                // A quiet gold pill in the lab's own hue; hidden when the rail
                // is collapsed, and outside the accessible name so the link
                // still reads as its label to a screen reader.
                <span aria-hidden="true" data-testid="nav-badge"
                      className={`ml-auto shrink-0 rounded-full border border-[#9A7B2F] bg-[#FBF8F0] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#9A7B2F] ${
                          collapsed ? 'lg:hidden' : ''
                      }`}>
                    {badge}
                </span>
            ) : null}
        </Link>
    );
}

/** Group heading. Every section reads the same way now.
 *
 *  Explore, Plan and Decide used to carry a stage number (N1 "journey rail",
 *  founder-approved 2026-08-09) on the argument that the sidebar IS the
 *  admissions process in order. The founder reversed it on 2026-09-21,
 *  looking at a sidebar that had grown a Build section which is not a stage
 *  at all: "lets remove the number 1, 2, 3 from Explore, Plan and Decide..
 *  they can be displayed like the other groups." Numbering three of six
 *  groups implied an order the sidebar no longer has.
 *
 *  STAGE_CHIP_CLASS went with them: its only other consumer was the Video
 *  Tours gallery, retired 2026-09-16. On the collapsed desktop rail a thin
 *  divider still stands in for the heading. */
function SectionHeading({ label, collapsed, first, tag }) {
    return (
        <>
            {collapsed && !first && (
                <div className="hidden lg:block mx-3 my-1.5 border-t border-[#E0DED8]/70" aria-hidden="true" />
            )}
            <div
                className={`flex items-center gap-2 px-3 ${first ? 'pt-0.5' : 'pt-3.5'} pb-1 ${
                    collapsed ? 'lg:hidden' : ''
                }`}
            >
                <span className="text-[10px] font-semibold uppercase tracking-[0.11em] text-[#9A968C]">{label}</span>
                {/* One Pass tag on the group, not one per row (founder,
                    2026-09-28: "we can tag the Group - Build itself as Pass..
                    that way the nav entries would look cleaner"). */}
                {tag ? (
                    <span data-testid="nav-group-tag" className="rounded-full border border-[#9A7B2F]/60 px-1.5 py-px text-[9px] font-semibold uppercase tracking-[0.08em] text-[#9A7B2F]">
                        {tag}
                    </span>
                ) : null}
                <span className="flex-1 border-t border-[#E0DED8]/70" aria-hidden="true" />
            </div>
        </>
    );
}

/** Footer link/button — shares the icon-only-when-collapsed treatment and the
 *  same per-icon tint the main nav items carry. */
function FooterLink({ to, label, icon: Icon, color, collapsed, primary, active, onNavigate }) {
    return (
        <Link
            to={to}
            onClick={onNavigate}
            title={collapsed ? label : undefined}
            aria-label={label}
            aria-current={active ? 'page' : undefined}
            className={`group flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] transition-all
                ${collapsed ? 'lg:justify-center lg:px-0' : ''}
                ${
                    primary
                        ? 'border border-[#E7C9BC] text-[#C05838] hover:bg-[#C05838] hover:text-white hover:border-[#C05838] font-semibold'
                        : active
                            ? 'bg-[#D6E8D5] text-[#1A4D2E] font-semibold'
                            : 'font-medium text-[#4A4A4A] hover:bg-[#F8F6F0] hover:text-[#1A4D2E]'
                }`}
        >
            {Icon && (
                <Icon
                    className="h-[18px] w-[18px] shrink-0 transition-colors"
                    style={primary ? undefined : { color: active ? '#1A4D2E' : (color || '#9AA8A0') }}
                    aria-hidden="true"
                />
            )}
            <span className={collapsed ? 'lg:hidden' : ''}>{label}</span>
        </Link>
    );
}

// Opens the concierge from the nav (N3 "Astra-anchored", founder-approved
// 2026-08-09): the dock dispatches; ConciergeLauncher listens and takes the
// student to /astra (#2210). The floating pill retires on desktop (it covered
// card CTAs on every long page) and remains the mobile entry.
//
// The name itself now lives in utils/astraLink.js — a leaf module the two
// components that used to retype the literal can import without pulling the
// nav into their chunk. Re-exported here because a dozen call sites and tests
// already import it from this module.
export { OPEN_ASTRA_EVENT } from '../utils/astraLink';

const Navigation = () => {
    const location = useLocation();
    // Astra Today (#3754): how many of the student's moves are open, for B2.
    const astraToday = useAstraToday();
    const astraThings = astraToday.enabled ? openMoves(astraToday.moves).length : 0;
    const { currentUser } = useAuth();
    const { isSeasonal, isTrial } = usePayment();
    const { collapsed, toggleCollapsed, mobileOpen, setMobileOpen } = useSidebar();

    const isActive = (path) =>
        path === '/resources'
            ? location.pathname.startsWith('/resources')
            : location.pathname === path;

    // Close the mobile drawer whenever the route changes.
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname, setMobileOpen]);

    const handleLogout = async () => {
        try {
            await logout();
        } catch (error) {
            console.error('Failed to log out', error);
        }
    };

    const closeMobile = () => setMobileOpen(false);

    // When logged out, only public links remain (today: just Resources), so a
    // section with no visible links is dropped entirely. Signed in, a
    // publicOnly link is hidden, and with it the Learn group (#5309).
    const { on: popularOn } = usePopularQuestions();
    const visible = (l) => (!l.requiresAuth || currentUser) && !(l.publicOnly && currentUser)
        && !(l.retiresWithPopular && popularOn);
    const visibleSections = NAV_SECTIONS
        .map((s) => ({
            ...s,
            links: s.links.filter(visible).map((l) => (l.children ? { ...l, children: l.children.filter(visible) } : l)),
        }))
        .filter((s) => s.links.length > 0);

    // What a row wears. The Build rows wear nothing (founder pick B, #5183):
    // no Start here, no "7 of 7", no New; a free account's one Pass tag sits
    // on the group heading instead (founder, 2026-09-28).
    // Comments on a shared link (#4987, design spec section 2): the Sharing
    // row carries an unread count while a reviewer's words wait unseen. The
    // link's accessible name says it, since the pill itself is hidden from
    // screen readers.
    const shareNotes = useShareNotes();
    const unreadNotes = shareNotes.inbox?.unread_total || 0;
    const badgeFor = (link) => {
        if (link.path === '/share' && unreadNotes > 0) {
            return { badge: `${unreadNotes} new`, ariaLabel: `${link.label}, ${unreadNotes} unread note${unreadNotes === 1 ? '' : 's'}` };
        }
        return {};
    };

    return (
        <>
            {/* Mobile-only slim top bar with the hamburger + logo. */}
            <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between h-14 px-4 bg-[#FDFCF7]/95 backdrop-blur-sm border-b border-[#E0DED8]">
                <button
                    type="button"
                    onClick={() => setMobileOpen(true)}
                    aria-label="Open navigation"
                    aria-expanded={mobileOpen}
                    className="-ml-2 p-2 rounded-lg text-[#4A4A4A] hover:bg-[#F8F6F0] hover:text-[#1A4D2E] transition-colors"
                >
                    <Bars3Icon className="h-6 w-6" />
                </button>
                <Link to="/" className="flex items-center" aria-label="Stratia Admissions home">
                    <img
                        src="/logo.png?v=2"
                        alt="Stratia Admissions"
                        className="h-10 w-auto max-w-[170px] object-contain mix-blend-multiply"
                    />
                </Link>
                <span className="w-8" aria-hidden="true" />
            </div>

            {/* Backdrop behind the mobile drawer. */}
            {mobileOpen && (
                <div
                    className="lg:hidden fixed inset-0 z-40 bg-black/30 backdrop-blur-[1px]"
                    onClick={closeMobile}
                    aria-hidden="true"
                />
            )}

            {/* The rail. Fixed on the left for all breakpoints; on mobile it slides
                off-canvas (translate) unless `mobileOpen`. On lg+ it is always
                visible and its WIDTH animates between collapsed/expanded. */}
            <nav
                aria-label="Primary"
                className={`fixed inset-y-0 left-0 z-50 flex flex-col w-64 bg-[#FDFCF7] border-r border-[#E0DED8]
                    transition-transform duration-300 ease-in-out
                    ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
                    lg:translate-x-0 lg:transition-[width] ${collapsed ? 'lg:w-16' : 'lg:w-60'}`}
            >
                {/* Header: brand + toggles. On the collapsed desktop rail the
                    whole header is the expand button (industry pattern: a single
                    compact chevron control, no text nav-row). */}
                <div className={`flex items-center h-16 px-3 border-b border-[#E0DED8] shrink-0 ${
                    collapsed ? 'lg:justify-center' : 'justify-between'
                }`}>
                    {/* Collapsed desktop rail: a centered expand toggle (replaces
                        the wordmark). Hidden on mobile, where the rail is full-width. */}
                    {collapsed && (
                        <button
                            type="button"
                            onClick={toggleCollapsed}
                            aria-label="Expand sidebar"
                            aria-expanded={false}
                            title="Expand sidebar"
                            className="hidden lg:flex h-10 w-10 items-center justify-center rounded-lg text-[#4A4A4A] hover:bg-[#F8F6F0] hover:text-[#1A4D2E] transition-colors"
                        >
                            <ChevronDoubleRightIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                    )}
                    {/* Wordmark — home link. Hidden on the collapsed desktop rail. */}
                    <Link
                        to="/"
                        onClick={closeMobile}
                        className={`flex items-center overflow-hidden ${collapsed ? 'lg:hidden' : ''}`}
                        aria-label="Stratia Admissions home"
                    >
                        <img
                            src="/logo.png?v=2"
                            alt="Stratia Admissions"
                            className="h-12 w-auto max-w-[200px] object-contain object-left mix-blend-multiply"
                        />
                    </Link>
                    {/* Desktop-only collapse toggle (expanded state), top-right. */}
                    <button
                        type="button"
                        onClick={toggleCollapsed}
                        aria-label="Collapse sidebar"
                        aria-expanded={true}
                        title="Collapse sidebar"
                        className={`hidden lg:flex p-2 rounded-lg text-[#4A4A4A] hover:bg-[#F8F6F0] hover:text-[#1A4D2E] transition-colors ${
                            collapsed ? 'lg:hidden' : ''
                        }`}
                    >
                        <ChevronDoubleLeftIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                    {/* Mobile close button. */}
                    <button
                        type="button"
                        onClick={closeMobile}
                        aria-label="Close navigation"
                        className="lg:hidden p-2 -mr-1 rounded-lg text-[#4A4A4A] hover:bg-[#F8F6F0] transition-colors"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                {/* Links — grouped by journey section. */}
                <div className="flex-1 overflow-y-auto px-2 py-2">
                    {visibleSections.map((section, i) => (
                        <div key={section.label} className="mb-1.5">
                            <SectionHeading label={section.label} collapsed={collapsed} first={i === 0}
                                tag={section.label === BUILD_NAMES.group && !isSeasonal ? 'Pass' : null} />
                            <div className="space-y-1">
                                {/* Astra, the counselor row (founder pick B from the
                                    sidebar mocks, 2026-09-15: "students don't even know
                                    if Astra is an option to click"). First row of You,
                                    two lines, filled at all times: in the group, named,
                                    and still the one row that looks like a person you can
                                    talk to. The green tile above the groups is gone. Same
                                    audience as the launcher: signed in. */}
                                {section.label === 'You' && currentUser && (
                                    <button
                                        type="button"
                                        data-testid="nav-astra-dock"
                                        onClick={() => {
                                            // The badge promises a step: the click opens Astra ON it (#3786).
                                            const move = topMoveId(astraToday);
                                            window.dispatchEvent(move
                                                ? new CustomEvent(OPEN_ASTRA_EVENT, { detail: { move } })
                                                : new Event(OPEN_ASTRA_EVENT));
                                            closeMobile();
                                        }}
                                        aria-label={astraThings ? `Ask Astra, ${thingsLine(astraThings)}` : 'Ask Astra'}
                                        aria-current={location.pathname === ASTRA_PATH ? 'page' : undefined}
                                        title={collapsed ? 'Ask Astra' : undefined}
                                        className={`w-full flex items-center gap-2.5 rounded-lg bg-[#1A4D2E] text-white
                                            px-3 py-2 text-left hover:bg-[#2D6B45] transition-colors
                                            ${location.pathname === ASTRA_PATH ? 'ring-2 ring-[#1A4D2E] ring-offset-2 ring-offset-[#FDFCF7]' : ''}
                                            ${collapsed ? 'lg:justify-center lg:px-0' : ''}`}
                                    >
                                        <AstraAvatar size={20} />
                                        <span className={`flex flex-col items-start leading-tight ${collapsed ? 'lg:hidden' : ''}`}>
                                            <span className="text-[13px] font-semibold">Astra</span>
                                            {/* B2 (founder pick, 2026-10-10): the second line
                                                says it in words when Astra Today holds a move
                                                for the student, with a small amber dot. No
                                                count to clear; a screen reader hears the words. */}
                                            {astraThings ? (
                                                <span className="flex items-center gap-1.5 text-[10.5px] font-semibold text-[#FFE2A6]"
                                                      data-testid="nav-astra-things">
                                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#F5B942]" aria-hidden="true" />
                                                    {thingsLine(astraThings)}
                                                </span>
                                            ) : (
                                                <span className="text-[10.5px] text-[#C7E3CB]">Ask any time</span>
                                            )}
                                        </span>
                                    </button>
                                )}
                                {section.links.map((link) => (
                                    <React.Fragment key={link.path}>
                                        <NavItem
                                            to={link.path}
                                            label={link.label}
                                            icon={link.icon}
                                            color={link.color}
                                            active={isActive(link.path)}
                                            collapsed={collapsed}
                                            onNavigate={closeMobile}
                                            // A free account sees the door before the click: the lab is
                                            // part of the Season Pass (founder pick A, 2026-09-22).
                                            {...badgeFor(link)}
                                        />
                                        {/* The steps nest under the home as a list a screen
                                            reader can enter and leave as one thing (#2394). */}
                                        {link.children && link.children.length > 0 ? (
                                            <ul role="list" aria-label={BUILD_NAMES.groupList} data-testid="nav-labs" className="m-0 list-none space-y-1 p-0">
                                                {link.children.map((child) => (
                                                    <li key={child.path}>
                                                        <NavItem
                                                            to={child.path}
                                                            label={child.label}
                                                            icon={child.icon}
                                                            color={child.color}
                                                            active={isActive(child.path)}
                                                            collapsed={collapsed}
                                                            onNavigate={closeMobile}
                                                            nested
                                                            {...badgeFor(child)}
                                                        />
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : null}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Footer: identity + actions, then the desktop collapse toggle. */}
                <div className="shrink-0 border-t border-[#E0DED8] p-2 space-y-0.5">
                    {currentUser ? (
                        <>
                            <div
                                className={`flex items-center gap-2.5 px-3 py-1.5 ${
                                    collapsed ? 'lg:justify-center lg:px-0' : ''
                                }`}
                                title={collapsed ? currentUser.displayName || currentUser.email : undefined}
                            >
                                {currentUser.photoURL ? (
                                    <img
                                        src={currentUser.photoURL}
                                        alt=""
                                        className="h-7 w-7 rounded-full ring-2 ring-[#D6E8D5] shrink-0"
                                    />
                                ) : (
                                    <span className="h-7 w-7 rounded-full bg-[#D6E8D5] text-[#1A4D2E] flex items-center justify-center text-[13px] font-semibold shrink-0">
                                        {(currentUser.displayName || currentUser.email || '?').charAt(0).toUpperCase()}
                                    </span>
                                )}
                                <span
                                    className={`text-[13px] text-[#4A4A4A] font-medium truncate ${
                                        collapsed ? 'lg:hidden' : ''
                                    }`}
                                >
                                    {currentUser.displayName || currentUser.email}
                                </span>
                            </div>
                            {/* Upgrade is the one monetization CTA — a soft
                                coral outline pill sets it apart from plain rows.
                                Hidden on the TOP tier (paid or comped season
                                pass — founder catch 2026-07-31: an Upgrade
                                button after a complimentary pass reads as a
                                billing glitch); free and monthly users still
                                have somewhere to upgrade TO. */}
                            {(!isSeasonal || isTrial) ? (
                                <FooterLink
                                    to="/pricing"
                                    label="Upgrade"
                                    icon={BoltIcon}
                                    primary
                                    collapsed={collapsed}
                                    active={isActive('/pricing')}
                                    onNavigate={() => { track(EVENTS.UPGRADE_CTA_CLICKED, { surface: 'sidebar' }); closeMobile(); }}
                                />
                            ) : (
                                /* Top-tier users still need a way to SEE their
                                   plan (founder catch 2026-07-31: hiding Upgrade
                                   removed the only path to /pricing, where
                                   "Active Plan / Complimentary through <date>"
                                   lives) — a quiet, non-CTA pill in its place. */
                                <FooterLink
                                    to="/pricing"
                                    label="Season Pass"
                                    icon={CheckBadgeIcon}
                                    collapsed={collapsed}
                                    active={isActive('/pricing')}
                                    onNavigate={closeMobile}
                                />
                            )}
                            {/* Share your story — the standing door for a
                                testimonial in the student's own words
                                (founder, 2026-09-04); the Launchpad ask is
                                one-time, this is always here. */}
                            <FooterLink
                                to="/share-your-story"
                                label="Share your story"
                                icon={ChatBubbleBottomCenterTextIcon}
                                color="#1A4D2E"
                                collapsed={collapsed}
                                active={isActive('/share-your-story')}
                                onNavigate={closeMobile}
                            />
                            <FooterLink
                                to="/settings"
                                label="Settings"
                                icon={Cog6ToothIcon}
                                color="#64748B"
                                collapsed={collapsed}
                                active={isActive('/settings')}
                                onNavigate={closeMobile}
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    closeMobile();
                                    handleLogout();
                                }}
                                title={collapsed ? 'Sign Out' : undefined}
                                aria-label="Sign Out"
                                className={`group w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium text-[#4A4A4A] hover:bg-[#F8F6F0] hover:text-[#1A4D2E] transition-all ${
                                    collapsed ? 'lg:justify-center lg:px-0' : ''
                                }`}
                            >
                                <ArrowRightOnRectangleIcon className="h-[18px] w-[18px] shrink-0 text-[#9AA8A0] group-hover:text-[#1A4D2E] transition-colors" aria-hidden="true" />
                                <span className={collapsed ? 'lg:hidden' : ''}>Sign Out</span>
                            </button>
                        </>
                    ) : (
                        <>
                            <FooterLink
                                to="/"
                                label="Sign in"
                                icon={ArrowRightOnRectangleIcon}
                                collapsed={collapsed}
                                onNavigate={closeMobile}
                            />
                            <FooterLink
                                to="/"
                                label="Try Stratia"
                                icon={StarIcon}
                                collapsed={collapsed}
                                primary
                                onNavigate={closeMobile}
                            />
                        </>
                    )}

                </div>
            </nav>

            {/* The floating feedback widget was retired 2026-08-02 (founder:
                "nobody uses it — anyway we ask for feedback"): contextual
                prompts (fit-analysis feedback, testimonial asks, the Contact
                page) are the feedback surface. The bottom-right corner now
                belongs to the Concierge. FeedbackModal itself lives on in
                FitAnalysisPage / FeedbackPage. */}
        </>
    );
};

export default Navigation;
